import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, EditIcon, TrashIcon, DownloadIcon, XIcon, CheckIcon, BarChart2Icon, LineChartIcon, TrendingUpIcon, PieChartIcon, PlusIcon, TicketIcon, SearchIcon } from 'lucide-react';
import { Campaign } from '../types/Campaign';
import { TimeSelector } from '../components/dashboard/TimeSelector';
import { ConversionFunnelChart, ConversionRatePieChart, CodeUsageTimeSeriesChart, PaymentVolumeTimeSeriesChart } from '../components/campaigns/CampaignCharts';
// Mock campaign data - would normally be fetched from API
const mockCampaigns: Record<string, Campaign> = {
  RBLSHOP2025: {
    id: 'RBLSHOP2025',
    title: '[RLife] 7-days Promotion for Shop',
    description: '[RLife] 7-days Promotion for Shop',
    expiryDate: '07/10/2025',
    discountPercentage: 0,
    fixedDiscountAmount: 50,
    currency: 'THB',
    discountCarriedBy: 'Merchant',
    merchantPercentage: 0,
    budget: 5000,
    budgetUsed: 0,
    strictlyEnforceBudget: true,
    validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    code: 'RBLSHOP2025',
    category: 'Discount code',
    owner: 'Merchant',
    codeStrategy: 'Global',
    minTransactionValue: 0,
    maxTransactionValue: 999999,
    minDiscount: 50,
    maxDiscount: 50,
    status: 'active',
    transactionVolume: 47250,
    transactionCount: 63
  },
  BBL10THB: {
    id: 'BBL10THB',
    title: 'BBL Special Package (THB 10 Discount)',
    description: 'DISCOUNT_COUPON',
    expiryDate: '02/08/2025',
    discountPercentage: 0,
    fixedDiscountAmount: 10,
    currency: 'THB',
    discountCarriedBy: 'Merchant',
    merchantPercentage: 0,
    budget: 11000,
    budgetUsed: 10960,
    strictlyEnforceBudget: true,
    validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    code: 'BBL10THB',
    category: 'Discount code',
    owner: 'Merchant',
    codeStrategy: 'Global',
    minTransactionValue: 0,
    maxTransactionValue: 999999,
    minDiscount: 10,
    maxDiscount: 10,
    status: 'active',
    transactionVolume: 1284500,
    transactionCount: 1096
  },
  BULK2025: {
    id: 'BULK2025',
    title: 'Summer Festival Bulk Codes',
    description: 'Unique codes for summer festival attendees',
    startDate: '01/06/2025',
    expiryDate: '31/08/2025',
    discountPercentage: 15,
    fixedDiscountAmount: 0,
    currency: 'THB',
    discountCarriedBy: 'Merchant',
    merchantPercentage: 0,
    budgetType: 'fixed',
    budget: 50000,
    budgetUsed: 12500,
    strictlyEnforceBudget: true,
    paymentMethods: ['Card', 'PromptPay'],
    cardTargetingMode: 'all',
    cardBrands: [],
    cardBin: '',
    cardIssuingCountry: '',
    cardType: 'All Types',
    validDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    code: '',
    category: 'Discount code',
    owner: 'Merchant',
    codeStrategy: 'Bulk',
    minTransactionValue: 500,
    maxTransactionValue: 999999,
    minDiscount: 0,
    maxDiscount: 1000,
    status: 'active',
    transactionVolume: 312750,
    transactionCount: 245
  }
};
// Mock Code Batches
const mockCodeBatches = [{
  id: 'BATCH-001',
  dateCreated: '15/07/2025',
  codesGenerated: 500,
  usageLimitPerCode: 1,
  codesUsed: 420,
  status: 'Active'
}, {
  id: 'BATCH-002',
  dateCreated: '20/07/2025',
  codesGenerated: 1000,
  usageLimitPerCode: 2,
  codesUsed: 150,
  status: 'Active'
}];
// Mock Individual Codes for a Batch
const mockIndividualCodes = [{
  code: 'BULK-A1B2C3D4',
  usedCount: 1,
  lastUsed: '25/07/2025 14:30',
  status: 'Fully Redeemed'
}, {
  code: 'BULK-E5F6G7H8',
  usedCount: 0,
  lastUsed: '-',
  status: 'Available'
}, {
  code: 'BULK-I9J0K1L2',
  usedCount: 1,
  lastUsed: '26/07/2025 09:15',
  status: 'Fully Redeemed'
}, {
  code: 'BULK-M3N4O5P6',
  usedCount: 0,
  lastUsed: '-',
  status: 'Available'
}, {
  code: 'BULK-Q7R8S9T0',
  usedCount: 1,
  lastUsed: '28/07/2025 18:45',
  status: 'Fully Redeemed'
}];
// Tab interface for analytics section
interface AnalyticsTab {
  id: string;
  label: string;
  icon: React.ReactNode;
}
export function CampaignDetails() {
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('conversion');
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportTimeframe, setExportTimeframe] = useState('30d');
  const [exportStatus, setExportStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  // Codes Tab State
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateCount, setGenerateCount] = useState(100);
  const [usageLimit, setUsageLimit] = useState(1);
  const [selectedBatch, setSelectedBatch] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const analyticsTabs: AnalyticsTab[] = [{
    id: 'conversion',
    label: 'Conversion',
    icon: <BarChart2Icon size={16} data-id="element-6256" />
  }, {
    id: 'usage',
    label: 'Code Usage',
    icon: <LineChartIcon size={16} data-id="element-6257" />
  }, {
    id: 'volume',
    label: 'Payment Volume',
    icon: <TrendingUpIcon size={16} data-id="element-6258" />
  }];
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const campaignId = params.get('id');
    if (campaignId && mockCampaigns[campaignId]) {
      setCampaign(mockCampaigns[campaignId]);
      // Auto-select first batch for bulk campaigns
      if (mockCampaigns[campaignId].codeStrategy === 'Bulk' && mockCodeBatches.length > 0) {
        setSelectedBatch(mockCodeBatches[0].id);
      }
    }
    setLoading(false);
  }, []);
  const navigateBack = () => {
    navigate('/campaigns');
  };
  const navigateToEdit = () => {
    if (campaign) {
      navigate(`/campaign-form?id=${campaign.id}`);
    }
  };
  const handleExport = () => {
    setExportStatus('loading');
    // Simulate export process
    setTimeout(() => {
      setExportStatus('success');
      // Reset after showing success message
      setTimeout(() => {
        setExportStatus('idle');
        setShowExportModal(false);
      }, 2000);
    }, 1000);
  };
  const handleGenerateCodes = () => {
    // Simulate generating codes
    console.log(`Generating ${generateCount} codes with limit ${usageLimit}`);
    setShowGenerateModal(false);
    // In a real app, this would refresh the batches list
  };
  // Calculate budget usage percentage
  const calculateBudgetUsage = () => {
    if (!campaign || campaign.budget === 0) return 0;
    return Math.round(campaign.budgetUsed / campaign.budget * 100);
  };
  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      active: 'bg-green-50 text-green-700 border border-green-100',
      inactive: 'bg-gray-100 text-gray-600 border border-gray-200',
      expired: 'bg-red-50 text-red-700 border border-red-100'
    };
    const style = styles[status] || styles.inactive;
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${style}`} data-id="element-6259">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>;
  };
  const getCodeStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      Available: 'bg-green-50 text-green-700 border border-green-100',
      'Fully Redeemed': 'bg-gray-100 text-gray-600 border border-gray-200'
    };
    const style = styles[status] || styles['Available'];
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${style}`} data-id="element-6260">
        {status}
      </span>;
  };
  if (loading) {
    return <div className="flex justify-center items-center h-64" data-id="element-6261">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F7941D]" data-id="element-6262"></div>
      </div>;
  }
  if (!campaign) {
    return <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center max-w-2xl mx-auto mt-12" data-id="element-6263">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-6264">
          Campaign not found
        </h2>
        <p className="text-gray-500 mb-6" data-id="element-6265">
          The campaign you are looking for does not exist or has been removed.
        </p>
        <button onClick={navigateBack} className="px-4 py-2 bg-[#F7941D] hover:bg-[#D97B06] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-6266">
          Back to Campaigns
        </button>
      </div>;
  }
  // Render analytics content based on active tab
  const renderAnalyticsContent = () => {
    switch (activeTab) {
      case 'conversion':
        return <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-6267">
            <div data-id="element-6268">
              <h3 className="text-sm font-medium text-gray-900 mb-6 uppercase tracking-wider" data-id="element-6269">
                Conversion Funnel
              </h3>
              <ConversionFunnelChart data-id="element-6270" />
            </div>
            <div data-id="element-6271">
              <h3 className="text-sm font-medium text-gray-900 mb-6 uppercase tracking-wider" data-id="element-6272">
                Conversion Rates
              </h3>
              <ConversionRatePieChart data-id="element-6273" />
            </div>
          </div>;
      case 'usage':
        return <div data-id="element-6274">
            <h3 className="text-sm font-medium text-gray-900 mb-6 uppercase tracking-wider" data-id="element-6275">
              Code Usage Over Time (Last 30 Days)
            </h3>
            <CodeUsageTimeSeriesChart data-id="element-6276" />
          </div>;
      case 'volume':
        return <div data-id="element-6277">
            <h3 className="text-sm font-medium text-gray-900 mb-6 uppercase tracking-wider" data-id="element-6278">
              Payment Volume Over Time (Last 30 Days)
            </h3>
            <PaymentVolumeTimeSeriesChart currency={campaign.currency} data-id="element-6279" />
          </div>;
      default:
        return null;
    }
  };
  return <div className="space-y-6 max-w-7xl mx-auto pb-12" data-id="element-6280">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-6281">
        <div className="flex items-center gap-4" data-id="element-6282">
          <button onClick={navigateBack} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-6283">
            <ArrowLeftIcon size={20} data-id="element-6284" />
          </button>
          <div data-id="element-6285">
            <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-3" data-id="element-6286">
              {campaign.title}
              {getStatusBadge(campaign.status)}
            </h1>
            <p className="text-sm text-gray-500 mt-1" data-id="element-6287">
              Campaign ID: {campaign.id}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3" data-id="element-6288">
          <button onClick={() => setShowExportModal(true)} className="flex items-center px-4 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-6289">
            <DownloadIcon size={16} className="mr-2" data-id="element-6290" />
            Export CSV
          </button>
          <button onClick={navigateToEdit} className="flex items-center px-4 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-6291">
            <EditIcon size={16} className="mr-2" data-id="element-6292" />
            Edit
          </button>
          <button className="flex items-center px-4 py-2 bg-white border border-red-200 text-red-600 text-sm font-medium rounded-lg hover:bg-red-50 transition-colors shadow-sm" data-id="element-6293">
            <TrashIcon size={16} className="mr-2" data-id="element-6294" />
            Delete
          </button>
        </div>
      </div>

      {/* Export Modal */}
      {showExportModal && <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" data-id="element-6295">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md border border-gray-200" data-id="element-6296">
            <div className="flex justify-between items-center mb-6" data-id="element-6297">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-6298">
                Export Campaign Data
              </h3>
              <button onClick={() => {
            setShowExportModal(false);
            setExportStatus('idle');
          }} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-6299">
                <XIcon size={20} data-id="element-6300" />
              </button>
            </div>
            <div className="mb-6" data-id="element-6301">
              <p className="text-sm font-medium text-gray-700 mb-2" data-id="element-6302">
                Select time period for export:
              </p>
              <TimeSelector value={exportTimeframe} onChange={setExportTimeframe} data-id="element-6303" />
            </div>
            <div className="mb-8 bg-gray-50 p-4 rounded-lg border border-gray-100" data-id="element-6304">
              <p className="text-sm font-medium text-gray-700 mb-2" data-id="element-6305">
                The export will include:
              </p>
              <ul className="space-y-1.5 text-sm text-gray-600" data-id="element-6306">
                <li className="flex items-center gap-2" data-id="element-6307">
                  <div className="w-1 h-1 rounded-full bg-gray-400" data-id="element-6308" />
                  Daily code usage statistics
                </li>
                <li className="flex items-center gap-2" data-id="element-6309">
                  <div className="w-1 h-1 rounded-full bg-gray-400" data-id="element-6310" />
                  Conversion metrics
                </li>
                <li className="flex items-center gap-2" data-id="element-6311">
                  <div className="w-1 h-1 rounded-full bg-gray-400" data-id="element-6312" />
                  Transaction volumes and amounts
                </li>
                <li className="flex items-center gap-2" data-id="element-6313">
                  <div className="w-1 h-1 rounded-full bg-gray-400" data-id="element-6314" />
                  User engagement data
                </li>
              </ul>
            </div>
            <div className="flex justify-end gap-3" data-id="element-6315">
              <button onClick={() => setShowExportModal(false)} className="px-4 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors" data-id="element-6316">
                Cancel
              </button>
              {exportStatus === 'idle' && <button onClick={handleExport} className="px-4 py-2 bg-[#F7941D] hover:bg-[#D97B06] text-white text-sm font-medium rounded-lg transition-colors shadow-sm flex items-center" data-id="element-6317">
                  <DownloadIcon size={16} className="mr-2" data-id="element-6318" />
                  Export Data
                </button>}
              {exportStatus === 'loading' && <button disabled className="px-4 py-2 bg-[#F7941D] text-white text-sm font-medium rounded-lg opacity-70 flex items-center cursor-not-allowed" data-id="element-6319">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/20 border-t-white mr-2" data-id="element-6320"></div>
                  Exporting...
                </button>}
              {exportStatus === 'success' && <button disabled className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg flex items-center cursor-not-allowed" data-id="element-6321">
                  <CheckIcon size={16} className="mr-2" data-id="element-6322" />
                  Export Complete
                </button>}
              {exportStatus === 'error' && <button onClick={handleExport} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm flex items-center" data-id="element-6323">
                  Try Again
                </button>}
            </div>
          </div>
        </div>}

      {/* Generate Codes Modal */}
      {showGenerateModal && <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" data-id="element-6324">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md border border-gray-200" data-id="element-6325">
            <div className="flex justify-between items-center mb-6" data-id="element-6326">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-6327">
                Generate Code Batch
              </h3>
              <button onClick={() => setShowGenerateModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-6328">
                <XIcon size={20} data-id="element-6329" />
              </button>
            </div>

            <div className="space-y-4 mb-8" data-id="element-6330">
              <div data-id="element-6331">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6332">
                  Number of Codes to Generate
                </label>
                <input type="number" min="1" max="10000" value={generateCount} onChange={e => setGenerateCount(parseInt(e.target.value) || 0)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#F7941D] focus:border-[#F7941D]" data-id="element-6333" />
              </div>
              <div data-id="element-6334">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6335">
                  Usage Limit Per Code
                </label>
                <input type="number" min="1" value={usageLimit} onChange={e => setUsageLimit(parseInt(e.target.value) || 0)} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#F7941D] focus:border-[#F7941D]" data-id="element-6336" />
                <p className="text-xs text-gray-500 mt-1" data-id="element-6337">
                  How many times each individual code can be redeemed.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3" data-id="element-6338">
              <button onClick={() => setShowGenerateModal(false)} className="px-4 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors" data-id="element-6339">
                Cancel
              </button>
              <button onClick={handleGenerateCodes} className="px-4 py-2 bg-[#F7941D] hover:bg-[#D97B06] text-white text-sm font-medium rounded-lg transition-colors shadow-sm flex items-center" data-id="element-6340">
                Generate Codes
              </button>
            </div>
          </div>
        </div>}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-6341">
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-6342">
          <div className="flex justify-between items-start mb-2" data-id="element-6343">
            <div className="text-sm font-medium text-gray-500" data-id="element-6344">
              Budget Usage
            </div>
            <div className="text-sm font-medium text-gray-900" data-id="element-6345">
              {calculateBudgetUsage()}%
            </div>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-1.5 mb-4 overflow-hidden" data-id="element-6346">
            <div className="bg-[#F7941D] h-1.5 rounded-full transition-all" style={{
            width: `${calculateBudgetUsage()}%`
          }} data-id="element-6347"></div>
          </div>
          <div className="text-2xl font-semibold text-gray-900" data-id="element-6348">
            {campaign.budgetUsed.toLocaleString('en-US')}
            <span className="text-sm font-medium text-gray-500 ml-1" data-id="element-6349">
              / {campaign.budget.toLocaleString('en-US')} {campaign.currency}
            </span>
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-6350">
          <div className="text-sm font-medium text-gray-500" data-id="element-6351">
            Transaction Volume
          </div>
          <div className="mt-2 text-2xl font-semibold text-gray-900" data-id="element-6352">
            {campaign.currency}{' '}
            {campaign.transactionVolume?.toLocaleString('en-US') || '0.00'}
          </div>
          <div className="mt-1 text-sm text-gray-500" data-id="element-6353">
            From {campaign.transactionCount || 0} transactions
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-6354">
          <div className="text-sm font-medium text-gray-500" data-id="element-6355">Volume ROI</div>
          <div className="mt-2 text-2xl font-semibold text-gray-900" data-id="element-6356">
            {campaign.budget > 0 ? `${((campaign.transactionVolume || 0) / campaign.budget).toFixed(1)}x` : '0x'}
          </div>
          <div className="mt-1 text-sm text-gray-500" data-id="element-6357">
            Against budget of {campaign.currency}{' '}
            {campaign.budget.toLocaleString('en-US')}
          </div>
        </div>
      </div>

      {/* Main Content Card - Details */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-6358">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center" data-id="element-6359">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider" data-id="element-6360">
            Campaign Details
          </h2>
        </div>
        <div className="p-6" data-id="element-6361">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-8" data-id="element-6362">
            <div data-id="element-6363">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-6364">
                Category
              </div>
              <div className="text-sm text-gray-900" data-id="element-6365">{campaign.category}</div>
            </div>
            <div data-id="element-6366">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-6367">
                Code Strategy
              </div>
              <div className="text-sm text-gray-900" data-id="element-6368">
                {campaign.codeStrategy}
              </div>
            </div>
            <div data-id="element-6369">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-6370">
                Campaign Code
              </div>
              <div className="text-sm text-gray-900 font-medium" data-id="element-6371">
                {campaign.code || 'N/A'}
              </div>
            </div>
            <div data-id="element-6372">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-6373">
                Expiry Date
              </div>
              <div className="text-sm text-gray-900" data-id="element-6374">{campaign.expiryDate}</div>
            </div>
            <div data-id="element-6375">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-6376">
                Discount
              </div>
              <div className="text-sm text-gray-900" data-id="element-6377">
                {campaign.discountPercentage > 0 ? `${campaign.discountPercentage}%` : `${campaign.fixedDiscountAmount} ${campaign.currency}`}
              </div>
            </div>
            <div data-id="element-6378">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-6379">
                Discount Carried By
              </div>
              <div className="text-sm text-gray-900" data-id="element-6380">
                {campaign.discountCarriedBy}
                {campaign.merchantPercentage ? ` (${campaign.merchantPercentage}% Merchant)` : ''}
              </div>
            </div>
            <div className="md:col-span-3" data-id="element-6381">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-6382">
                Description
              </div>
              <div className="text-sm text-gray-900" data-id="element-6383">
                {campaign.description || 'No description provided.'}
              </div>
            </div>
          </div>
        </div>

        {/* Constraints & Targeting Section */}
        <div className="p-6 border-t border-gray-100" data-id="element-6384">
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-6" data-id="element-6385">
            Constraints & Targeting
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-8" data-id="element-6386">
            <div data-id="element-6387">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-6388">
                Start Date
              </div>
              <div className="text-sm text-gray-900" data-id="element-6389">
                {campaign.startDate || 'Not set'}
              </div>
            </div>
            <div data-id="element-6390">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-6391">
                Transaction Value Limits
              </div>
              <div className="text-sm text-gray-900" data-id="element-6392">
                {campaign.currency}{' '}
                {campaign.minTransactionValue.toLocaleString()} –{' '}
                {campaign.currency}{' '}
                {campaign.maxTransactionValue.toLocaleString()}
              </div>
            </div>
            <div data-id="element-6393">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-6394">
                Discount Limits
              </div>
              <div className="text-sm text-gray-900" data-id="element-6395">
                {campaign.currency} {campaign.minDiscount.toLocaleString()} –{' '}
                {campaign.currency} {campaign.maxDiscount.toLocaleString()}
              </div>
            </div>
            <div data-id="element-6396">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-6397">
                Valid Days
              </div>
              <div className="text-sm text-gray-900" data-id="element-6398">
                {campaign.validDays.length === 7 ? 'All days' : campaign.validDays.join(', ')}
              </div>
            </div>
            <div data-id="element-6399">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-6400">
                Payment Methods
              </div>
              <div className="text-sm text-gray-900" data-id="element-6401">
                {campaign.paymentMethods && campaign.paymentMethods.length > 0 ? campaign.paymentMethods.join(', ') : 'All payment methods'}
              </div>
            </div>
            <div data-id="element-6402">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-6403">
                Budget Enforcement
              </div>
              <div className="text-sm text-gray-900" data-id="element-6404">
                {campaign.strictlyEnforceBudget ? 'Strictly enforced' : 'Soft limit'}
              </div>
            </div>
            <div data-id="element-6405">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-6406">
                Budget Type
              </div>
              <div className="text-sm text-gray-900" data-id="element-6407">
                {campaign.budgetType === 'recurring' ? `Recurring (${campaign.recurringBudgetPeriod || 'monthly'})` : 'Fixed'}
              </div>
            </div>
            <div data-id="element-6408">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-6409">
                Customer Usage Limits
              </div>
              <div className="text-sm text-gray-900" data-id="element-6410">
                {campaign.limitUsage ? `Limited to: ${campaign.limitUsage}` : 'No usage limits'}
              </div>
            </div>
            <div data-id="element-6411">
              <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-6412">
                Card Targeting
              </div>
              <div className="text-sm text-gray-900" data-id="element-6413">
                {campaign.paymentMethods?.includes('Card') ? (() => {
                const rules: string[] = [];
                if (campaign.cardBrands && campaign.cardBrands.length > 0) rules.push(`Brands: ${campaign.cardBrands.join(', ')}`);
                if (campaign.cardType && campaign.cardType !== 'All Types') rules.push(`Type: ${campaign.cardType}`);
                if (campaign.cardBin) rules.push(`BINs: ${campaign.cardBin}`);
                if (campaign.cardIssuingCountry) rules.push(`Country: ${campaign.cardIssuingCountry}`);
                return rules.length > 0 ? rules.join(' • ') : 'All cards accepted';
              })() : 'N/A'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Campaign Analytics Section */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-6414">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border-b border-gray-200 gap-4" data-id="element-6415">
          <div className="flex items-center gap-2" data-id="element-6416">
            <PieChartIcon size={20} className="text-gray-400" data-id="element-6417" />
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider" data-id="element-6418">
              Analytics
            </h2>
          </div>

          {/* Analytics Tabs */}
          <div className="flex space-x-6 overflow-x-auto" data-id="element-6419">
            {analyticsTabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`pb-4 -mb-4 text-sm font-medium transition-colors border-b-2 flex items-center gap-2 whitespace-nowrap ${activeTab === tab.id ? 'border-[#F7941D] text-[#F7941D]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-6420">
                {tab.icon}
                {tab.label}
              </button>)}
          </div>
        </div>

        <div className="p-6" data-id="element-6421">{renderAnalyticsContent()}</div>
      </div>

      {/* Code Batches Section - Standalone for Bulk campaigns */}
      {campaign.codeStrategy === 'Bulk' && <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-6422">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-6423">
            <div className="flex items-center gap-2" data-id="element-6424">
              <TicketIcon size={20} className="text-gray-400" data-id="element-6425" />
              <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider" data-id="element-6426">
                Code Batches
              </h2>
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full" data-id="element-6427">
                {mockCodeBatches.length} batches
              </span>
            </div>
            <button onClick={() => setShowGenerateModal(true)} className="flex items-center px-3 py-1.5 bg-[#F7941D] text-white text-sm font-medium rounded-lg hover:bg-[#D97B06] transition-colors shadow-sm" data-id="element-6428">
              <PlusIcon size={16} className="mr-1.5" data-id="element-6429" />
              Generate Batch
            </button>
          </div>

          {/* Batch Summary Cards */}
          <div className="p-4 border-b border-gray-100 bg-gray-50/30" data-id="element-6430">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-6431">
              <div className="bg-white rounded-lg border border-gray-200 p-3" data-id="element-6432">
                <div className="text-xs font-medium text-gray-500 mb-1" data-id="element-6433">
                  Total Codes
                </div>
                <div className="text-lg font-semibold text-gray-900" data-id="element-6434">
                  {mockCodeBatches.reduce((sum, b) => sum + b.codesGenerated, 0).toLocaleString()}
                </div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-3" data-id="element-6435">
                <div className="text-xs font-medium text-gray-500 mb-1" data-id="element-6436">
                  Total Redeemed
                </div>
                <div className="text-lg font-semibold text-gray-900" data-id="element-6437">
                  {mockCodeBatches.reduce((sum, b) => sum + b.codesUsed, 0).toLocaleString()}
                </div>
              </div>
              <div className="bg-white rounded-lg border border-gray-200 p-3" data-id="element-6438">
                <div className="text-xs font-medium text-gray-500 mb-1" data-id="element-6439">
                  Redemption Rate
                </div>
                <div className="text-lg font-semibold text-gray-900" data-id="element-6440">
                  {Math.round(mockCodeBatches.reduce((sum, b) => sum + b.codesUsed, 0) / mockCodeBatches.reduce((sum, b) => sum + b.codesGenerated, 0) * 100)}
                  %
                </div>
              </div>
            </div>
          </div>

          {/* Batches Table */}
          <div className="overflow-x-auto" data-id="element-6441">
            <table className="w-full text-left border-collapse" data-id="element-6442">
              <thead data-id="element-6443">
                <tr className="border-b border-gray-100 bg-gray-50/50" data-id="element-6444">
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6445">
                    Batch ID
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6446">
                    Date Created
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6447">
                    Codes Generated
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6448">
                    Usage Limit
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6449">
                    Redemption
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-6450">
                    Status
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-6451">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-6452">
                {mockCodeBatches.map(batch => <tr key={batch.id} onClick={() => setSelectedBatch(selectedBatch === batch.id ? null : batch.id)} className={`cursor-pointer transition-colors ${selectedBatch === batch.id ? 'bg-orange-50/50' : 'hover:bg-gray-50'}`} data-id="element-6453">
                    <td className="px-6 py-4" data-id="element-6454">
                      <span className="text-sm font-medium text-gray-900 font-mono" data-id="element-6455">
                        {batch.id}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500" data-id="element-6456">
                      {batch.dateCreated}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 font-medium" data-id="element-6457">
                      {batch.codesGenerated.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500" data-id="element-6458">
                      {batch.usageLimitPerCode}× per code
                    </td>
                    <td className="px-6 py-4" data-id="element-6459">
                      <div className="flex items-center gap-3" data-id="element-6460">
                        <div className="w-20 bg-gray-200 rounded-full h-1.5 overflow-hidden" data-id="element-6461">
                          <div className="bg-blue-500 h-1.5 rounded-full" style={{
                      width: `${batch.codesUsed / batch.codesGenerated * 100}%`
                    }} data-id="element-6462" />
                        </div>
                        <span className="text-sm text-gray-700 font-medium" data-id="element-6463">
                          {batch.codesUsed}/{batch.codesGenerated}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-6464">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-green-50 text-green-700 border border-green-100" data-id="element-6465">
                        {batch.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-6466">
                      <button className="text-gray-400 hover:text-gray-600 transition-colors p-1" data-id="element-6467">
                        <DownloadIcon size={14} data-id="element-6468" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          {/* Expanded Individual Codes for selected batch */}
          {selectedBatch && <div className="border-t border-gray-200" data-id="element-6469">
              <div className="p-4 bg-gray-50 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3" data-id="element-6470">
                <div data-id="element-6471">
                  <h3 className="text-sm font-semibold text-gray-900" data-id="element-6472">
                    Individual Codes — {selectedBatch}
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5" data-id="element-6473">
                    {mockIndividualCodes.length} codes shown •{' '}
                    {mockIndividualCodes.filter(c => c.status === 'Available').length}{' '}
                    available,{' '}
                    {mockIndividualCodes.filter(c => c.status === 'Fully Redeemed').length}{' '}
                    redeemed
                  </p>
                </div>
                <div className="flex gap-2" data-id="element-6474">
                  <div className="relative" data-id="element-6475">
                    <SearchIcon size={14} className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-6476" />
                    <input type="text" placeholder="Search code..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#F7941D] focus:border-[#F7941D] bg-white" data-id="element-6477" />
                  </div>
                  <button className="flex items-center px-3 py-1.5 border border-gray-200 text-gray-700 text-sm font-medium rounded-md hover:bg-white transition-colors bg-white" data-id="element-6478">
                    <DownloadIcon size={14} className="mr-1.5" data-id="element-6479" />
                    Export Codes
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto" data-id="element-6480">
                <table className="w-full text-left border-collapse" data-id="element-6481">
                  <thead className="bg-gray-50/50" data-id="element-6482">
                    <tr data-id="element-6483">
                      <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-6484">
                        Code
                      </th>
                      <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-6485">
                        Times Used
                      </th>
                      <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-6486">
                        Last Used
                      </th>
                      <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200 text-right" data-id="element-6487">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100" data-id="element-6488">
                    {mockIndividualCodes.filter(code => !searchTerm || code.code.toLowerCase().includes(searchTerm.toLowerCase())).map((code, idx) => <tr key={idx} className="hover:bg-gray-50" data-id="element-6489">
                          <td className="px-6 py-3 text-sm font-medium text-gray-900 font-mono" data-id="element-6490">
                            {code.code}
                          </td>
                          <td className="px-6 py-3 text-sm text-gray-500" data-id="element-6491">
                            {code.usedCount}
                          </td>
                          <td className="px-6 py-3 text-sm text-gray-500" data-id="element-6492">
                            {code.lastUsed}
                          </td>
                          <td className="px-6 py-3 text-right" data-id="element-6493">
                            {getCodeStatusBadge(code.status)}
                          </td>
                        </tr>)}
                  </tbody>
                </table>
              </div>
            </div>}
        </div>}
    </div>;
}